import React, { useState } from 'react';
import { Save } from 'lucide-react';
import dayjs from 'dayjs';
import type { NotificationFormData } from '../../types/notification';
import { VERTICALS } from '../../../../theme/constants';

interface NotificationFormProps {
  initialData?: NotificationFormData;
  onSubmit: (data: NotificationFormData) => Promise<void>;
  onCancel: () => void;
  businesses: Array<{ id: string; name: string }>;
  loading: boolean;
}

const NotificationForm: React.FC<NotificationFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  businesses,
  loading
}) => {
  const [formData, setFormData] = useState<NotificationFormData>(initialData || {
    title: '',
    message: '',
    targetType: 'all',
    startDate: new Date(),
    isDismissible: true,
    status: 'active'
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value ? new Date(value) : null
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-content-2">
          Título
        </label>
        <input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          required
          className="mt-1 block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
        />
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-content-2">
          Mensaje
        </label>
        <div className="mt-1">
          <textarea
            id="message"
            name="message"
            rows={4}
            value={formData.message}
            onChange={handleInputChange}
            required
            className="block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
          />
        </div>
      </div>

      <div>
        <label htmlFor="targetType" className="block text-sm font-medium text-content-2">
          Destinatarios
        </label>
        <select
          id="targetType"
          name="targetType"
          value={formData.targetType}
          onChange={handleInputChange}
          className="mt-1 block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
        >
          <option value="all">Todos los negocios</option>
          <option value="vertical">Por vertical</option>
          <option value="specific">Negocio específico</option>
        </select>
      </div>

      {formData.targetType === 'vertical' && (
        <div>
          <label htmlFor="targetValue" className="block text-sm font-medium text-content-2">
            Vertical
          </label>
          <select
            id="targetValue"
            name="targetValue"
            value={formData.targetValue}
            onChange={handleInputChange}
            required
            className="mt-1 block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
          >
            <option value="">Seleccionar vertical...</option>
            {VERTICALS.map(v => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {formData.targetType === 'specific' && (
        <div>
          <label htmlFor="targetValue" className="block text-sm font-medium text-content-2">
            Negocio
          </label>
          <select
            id="targetValue"
            name="targetValue"
            value={formData.targetValue}
            onChange={handleInputChange}
            required
            className="mt-1 block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
          >
            <option value="">Seleccionar negocio...</option>
            {businesses.map(business => (
              <option key={business.id} value={business.id}>
                {business.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-content-2">
            Fecha de inicio
          </label>
          <input
            type="datetime-local"
            id="startDate"
            name="startDate"
            value={dayjs(formData.startDate).format('YYYY-MM-DDTHH:mm')}
            onChange={handleDateChange}
            required
            className="mt-1 block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-content-2">
            Fecha de fin (opcional)
          </label>
          <input
            type="datetime-local"
            id="endDate"
            name="endDate"
            value={formData.endDate ? dayjs(formData.endDate).format('YYYY-MM-DDTHH:mm') : ''}
            onChange={handleDateChange}
            className="mt-1 block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
          />
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="isDismissible"
          name="isDismissible"
          checked={formData.isDismissible}
          onChange={handleCheckboxChange}
          className="h-4 w-4 text-accent focus:ring-accent border-line rounded"
        />
        <label htmlFor="isDismissible" className="ml-2 block text-sm text-content">
          Permitir que el mensaje sea descartado
        </label>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex justify-center py-2 px-4 border border-line shadow-sm text-sm font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-accent-solid hover:bg-accent-solid/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50"
        >
          <Save className="h-4 w-4 mr-2" />
          {loading ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </form>
  );
};

export default NotificationForm;